import { getPlatformCurrency } from "../../services/currency";

export const SUPREMA_PLATFORM = "Suprema";
export const SUPREMA_ACCOUNT_LIMIT = 10;

export const createAccountId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createPlatformAccount = (name, alias = "") => ({
  id: createAccountId(),
  name,
  alias: alias.trim(),
  balance: "",
  balanceCurrency: getPlatformCurrency(name),
  withdrawn: "",
  withdrawnCurrency: getPlatformCurrency(name),
  deposited: "",
  depositedCurrency: getPlatformCurrency(name),
});

export const getAccountLabel = (account) =>
  account.alias ? `${account.name} - ${account.alias}` : account.name;

export const accountHasFinancialValue = (account) =>
  ["balance", "withdrawn", "deposited"].some((field) => {
    const value = Number(String(account?.[field] || "").replace(",", "."));
    return Number.isFinite(value) && value > 0;
  });

export const platformHasFinancialValue = (accounts, platform) =>
  accounts.some(
    (account) => account.name === platform && accountHasFinancialValue(account)
  );

export const getSupremaAliasOrFallback = (accounts, aliasDraft) => {
  const alias = aliasDraft.trim();
  if (alias) return alias;

  const usedAliases = new Set(
    accounts
      .filter((account) => account.name === SUPREMA_PLATFORM)
      .map((account) =>
        (account.alias || "Conta 1").toLocaleLowerCase("pt-BR")
      )
  );
  if (accounts.some((account) => account.name === SUPREMA_PLATFORM)) {
    usedAliases.add("conta 1");
  }

  for (let index = 1; index <= SUPREMA_ACCOUNT_LIMIT; index += 1) {
    const fallback = `Conta ${index}`;
    if (!usedAliases.has(fallback.toLocaleLowerCase("pt-BR"))) return fallback;
  }

  return `Conta ${accounts.length + 1}`;
};

export const getUniqueSupremaAlias = (accounts, aliasDraft, accountIdToIgnore = null) => {
  const comparableAccounts = accountIdToIgnore
    ? accounts.filter((account) => account.id !== accountIdToIgnore)
    : accounts;
  const preferredAlias = getSupremaAliasOrFallback(comparableAccounts, aliasDraft);
  const aliasExists = accounts.some(
    (account) =>
      account.id !== accountIdToIgnore &&
      account.name === SUPREMA_PLATFORM &&
      (account.alias || "Conta 1").localeCompare(preferredAlias, "pt-BR", {
        sensitivity: "base",
      }) === 0
  );

  if (!aliasExists) return preferredAlias;
  return getSupremaAliasOrFallback(comparableAccounts, "");
};

export const compareAccounts = (a, b) => {
  const aIsSuprema = a.name === SUPREMA_PLATFORM;
  const bIsSuprema = b.name === SUPREMA_PLATFORM;
  if (aIsSuprema !== bIsSuprema) return aIsSuprema ? 1 : -1;
  return getAccountLabel(a).localeCompare(getAccountLabel(b), "pt-BR");
};

export const compareAccountsWithPrimarySuprema = (primarySupremaAccountId, orderedAccounts) => {
  const accountOrder = new Map(
    orderedAccounts.map((account, index) => [account.id, index])
  );

  return (a, b) => {
    const baseOrder = compareAccounts(a, b);
    if (a.name !== SUPREMA_PLATFORM || b.name !== SUPREMA_PLATFORM) {
      return baseOrder;
    }

    const aIsPrimary = a.id === primarySupremaAccountId;
    const bIsPrimary = b.id === primarySupremaAccountId;
    if (aIsPrimary !== bIsPrimary) return aIsPrimary ? -1 : 1;
    return (accountOrder.get(a.id) ?? 0) - (accountOrder.get(b.id) ?? 0);
  };
};

export const comparePlatformNames = (a, b) => {
  const aIsSuprema = a === SUPREMA_PLATFORM;
  const bIsSuprema = b === SUPREMA_PLATFORM;
  if (aIsSuprema !== bIsSuprema) return aIsSuprema ? 1 : -1;
  return a.localeCompare(b, "pt-BR");
};

export const getSupremaDisplayAlias = (account) => account.alias || "Conta 1";
